import { useEffect, useState } from "react";
import { Save, Loader2, Eye, Trash2, Brush, AlertTriangle, Zap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { adminApi } from "@/lib/api";

const intervalOptions = [
  { value: 0, label: "Jamais (désactivé)" },
  { value: 6, label: "6 mois" },
  { value: 12, label: "12 mois" },
  { value: 24, label: "24 mois" },
];

// Force-purge presets — independent from the automatic schedule above
const forceOptions = [
  { value: 1, label: "+ 1 mois" },
  { value: 3, label: "+ 3 mois" },
  { value: 6, label: "+ 6 mois" },
  { value: 12, label: "+ 12 mois" },
  { value: 24, label: "+ 24 mois" },
  { value: "ALL", label: "Toutes les opérations envoyées" },
];

const fmtBytes = (bytes) => {
  if (!bytes) return "0 octets";
  const units = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${units[i]}`;
};

export default function AdminPurge() {
  const [interval, setIntervalValue] = useState(12);
  const [lastPurge, setLastPurge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [orphanData, setOrphanData] = useState(null);
  const [busy, setBusy] = useState(false);

  // Force-purge (independent from the saved interval)
  const [forceChoice, setForceChoice] = useState(1);
  const [forcePreview, setForcePreview] = useState(null);
  const [forceBusy, setForceBusy] = useState(false);
  // Stage tracks the current step in the force-purge flow so the UI can
  // surface explicit feedback (idle → running → done). Without this the
  // user saw no indication that the purge had even started.
  const [forceStage, setForceStage] = useState("idle"); // "idle" | "previewing" | "running" | "done"
  const [forceResult, setForceResult] = useState(null);

  useEffect(() => {
    adminApi
      .purgeSettings()
      .then((d) => {
        setIntervalValue(d.purge_interval_months ?? 12);
        setLastPurge(d.last_purge_at);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updatePurgeSettings({ purge_interval_months: interval });
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setBusy(true);
    try {
      const d = await adminApi.previewPurge();
      setPreviewData(d);
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleRun = async () => {
    if (
      !previewData?.operations_count ||
      !window.confirm(
        `Supprimer les médias de ${previewData.operations_count} opération(s) (~${fmtBytes(previewData.media_bytes_estimate)}) ? Le PDF reste accessible via l'email.`
      )
    )
      return;
    setBusy(true);
    try {
      const r = await adminApi.runPurge();
      toast.success(`Purge effectuée : ${r.purged_operations} op., ~${fmtBytes(r.freed_bytes_estimate)} libéré`);
      setPreviewData(null);
      setLastPurge(r.last_purge_at);
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleOrphanPreview = async () => {
    setBusy(true);
    try {
      const d = await adminApi.previewOrphans();
      setOrphanData(d);
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleOrphanRun = async () => {
    if (
      !orphanData?.operations_count ||
      !window.confirm(
        `Supprimer définitivement ${orphanData.operations_count} opération(s) orpheline(s) ?`
      )
    )
      return;
    setBusy(true);
    try {
      const r = await adminApi.runOrphans();
      toast.success(`${r.deleted_operations} orpheline(s) supprimée(s)`);
      setOrphanData(null);
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  // ----- Force purge (manual override) -----
  const buildForcePayload = () =>
    forceChoice === "ALL"
      ? { force_all: true }
      : { interval_months: Number(forceChoice) };

  const handleForcePreview = async () => {
    setForceBusy(true);
    setForceStage("previewing");
    setForceResult(null);
    try {
      const d = await adminApi.previewPurge(buildForcePayload());
      setForcePreview(d);
      setForceStage("idle");
    } catch (err) {
      setForceStage("idle");
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setForceBusy(false);
    }
  };

  const handleForceRun = async () => {
    // If the user clicks "Forcer la purge" without previewing first, run the
    // preview implicitly so we still know how much we're about to wipe.
    let preview = forcePreview;
    if (!preview) {
      setForceBusy(true);
      setForceStage("previewing");
      try {
        preview = await adminApi.previewPurge(buildForcePayload());
        setForcePreview(preview);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erreur lors de la prévisualisation");
        setForceStage("idle");
        setForceBusy(false);
        return;
      }
      setForceBusy(false);
    }
    if (!preview?.operations_count) {
      setForceStage("idle");
      toast.info("Aucune opération éligible à purger avec ces critères");
      return;
    }
    const label = forceChoice === "ALL"
      ? "TOUTES les opérations envoyées par email (sans filtre de date)"
      : `les opérations de plus de ${forceChoice} mois`;
    if (
      !window.confirm(
        `Forcer la purge de ${preview.operations_count} opération(s) (${label}) — ~${fmtBytes(preview.media_bytes_estimate)} libérés ?\n\nLes PDF restent accessibles via les emails déjà envoyés.\nCette action est irréversible.`
      )
    ) {
      setForceStage("idle");
      return;
    }
    setForceBusy(true);
    setForceStage("running");
    setForceResult(null);
    // Loud start signal so the user knows the API call is in flight.
    const startToast = toast.loading(
      `Purge en cours… ${preview.operations_count} opération(s) à traiter`
    );
    try {
      const r = await adminApi.runPurge(buildForcePayload());
      toast.dismiss(startToast);
      toast.success(
        `Purge effectuée : ${r.purged_operations} opération(s) purgée(s), ~${fmtBytes(r.freed_bytes_estimate)} libéré(s)`
      );
      setForceResult({
        purged_operations: r.purged_operations,
        freed_bytes_estimate: r.freed_bytes_estimate,
        last_purge_at: r.last_purge_at,
        scope_label: label,
      });
      setForceStage("done");
      setForcePreview(null);
      setLastPurge(r.last_purge_at);
    } catch (err) {
      toast.dismiss(startToast);
      toast.error(err?.response?.data?.detail || "Erreur lors de la purge");
      setForceStage("idle");
    } finally {
      setForceBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-[var(--vidoc-text-secondary)]">Chargement...</div>;

  return (
    <div data-testid="admin-purge-page">
      <PageHeader
        title="Purge & nettoyage"
        subtitle="Libère de l'espace en supprimant les anciens médias"
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-6">
        {/* Settings */}
        <section className="bg-white rounded-xl border border-[var(--vidoc-border)] p-5 space-y-3">
          <h2 className="text-sm font-semibold">Purge automatique</h2>
          <p className="text-sm text-[var(--vidoc-text-secondary)]">
            Supprime les médias des opérations dont l'email a été envoyé et qui sont plus anciennes que l'intervalle choisi. Le PDF reste accessible via l'email envoyé.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {intervalOptions.map((o) => (
              <button
                key={o.value}
                data-testid={`interval-${o.value}`}
                onClick={() => setIntervalValue(o.value)}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium ${
                  interval === o.value
                    ? "bg-[var(--vidoc-primary)] text-white"
                    : "bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)]"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {lastPurge && (
            <p className="text-xs text-[var(--vidoc-text-tertiary)]">
              Dernière purge : {new Date(lastPurge).toLocaleString("fr-FR")}
            </p>
          )}
          <button
            data-testid="purge-settings-save"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-[var(--vidoc-primary)] text-white text-sm font-medium touch-target disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </section>

        {/* Manual purge (uses saved interval) */}
        <section className="bg-white rounded-xl border border-[var(--vidoc-border)] p-5 space-y-3">
          <h2 className="text-sm font-semibold">Purge manuelle (selon l'intervalle ci-dessus)</h2>
          <p className="text-xs text-[var(--vidoc-text-secondary)]">
            Utilise l'intervalle automatique enregistré ({interval === 0 ? "désactivé" : `${interval} mois`}). Pour ignorer cet intervalle, utilisez la section <strong>« Forcer une purge immédiate »</strong> ci-dessous.
          </p>
          <div className="flex gap-2">
            <button
              data-testid="purge-preview-btn"
              onClick={handlePreview}
              disabled={busy}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)] text-sm font-medium touch-target"
            >
              <Eye className="w-4 h-4" />
              Prévisualiser
            </button>
            <button
              data-testid="purge-run-btn"
              onClick={handleRun}
              disabled={busy || !previewData?.operations_count}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--vidoc-danger)] text-white text-sm font-medium touch-target disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Exécuter la purge
            </button>
          </div>
          {previewData && (
            <div className="bg-[var(--vidoc-bg-secondary)] rounded-lg p-3 text-sm">
              {previewData.message ? (
                <div>{previewData.message}</div>
              ) : (
                <>
                  <div>
                    <strong>{previewData.operations_count}</strong> opération(s) éligible(s) (intervalle : {previewData.interval_months} mois)
                  </div>
                  <div>Espace estimé libéré : <strong>{fmtBytes(previewData.media_bytes_estimate)}</strong></div>
                </>
              )}
            </div>
          )}
        </section>

        {/* Force purge (override interval) */}
        <section
          data-testid="force-purge-section"
          className="bg-orange-50/40 rounded-xl border border-orange-200 p-5 space-y-3"
        >
          <div className="flex items-start gap-2">
            <Zap className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
            <div>
              <h2 className="text-sm font-semibold text-orange-900">Forcer une purge immédiate</h2>
              <p className="text-xs text-[var(--vidoc-text-secondary)]">
                Indépendant de l'intervalle automatique. Choisissez la période ou purgez <strong>toutes</strong> les opérations dont l'email a été envoyé.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {forceOptions.map((o) => (
              <button
                key={o.value}
                data-testid={`force-option-${o.value}`}
                onClick={() => {
                  setForceChoice(o.value);
                  setForcePreview(null);
                }}
                className={`px-2.5 py-2 rounded-lg text-xs font-medium ${
                  forceChoice === o.value
                    ? "bg-orange-600 text-white"
                    : "bg-white border border-orange-200 text-orange-900 hover:bg-orange-100"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              data-testid="force-purge-preview-btn"
              onClick={handleForcePreview}
              disabled={forceBusy}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-orange-200 hover:bg-orange-100 text-sm font-medium touch-target"
            >
              {forceBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Prévisualiser
            </button>
            <button
              data-testid="force-purge-run-btn"
              onClick={handleForceRun}
              disabled={forceBusy}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium touch-target disabled:opacity-60"
            >
              {forceStage === "running" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Purge en cours…
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  Forcer la purge
                </>
              )}
            </button>
          </div>

          {/* Live "in-flight" banner — gives the user an immediate visual cue
              that the backend is working on the purge. */}
          {forceStage === "running" && (
            <div
              data-testid="force-purge-running-banner"
              className="bg-orange-100 border border-orange-300 rounded-lg p-3 text-sm flex items-center gap-2 text-orange-900"
            >
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <span>
                Purge en cours, veuillez patienter… La page se mettra à jour
                automatiquement à la fin.
              </span>
            </div>
          )}

          {/* Persistent success panel — replaces the ephemeral toast so the
              user can come back to the page and still see what was done. */}
          {forceStage === "done" && forceResult && (
            <div
              data-testid="force-purge-result"
              className="bg-green-50 border border-green-300 rounded-lg p-4 text-sm space-y-1"
            >
              <div className="flex items-center gap-2 text-green-800 font-semibold">
                <CheckCircle2 className="w-4 h-4" />
                Purge terminée avec succès
              </div>
              <div className="text-green-900">
                <strong>{forceResult.purged_operations}</strong> opération(s) purgée(s) ·{" "}
                <strong>~{fmtBytes(forceResult.freed_bytes_estimate)}</strong> libéré(s)
              </div>
              <div className="text-xs text-green-700">
                Critère : {forceResult.scope_label}
              </div>
              {forceResult.last_purge_at && (
                <div className="text-xs text-green-700">
                  Effectué le {new Date(forceResult.last_purge_at).toLocaleString("fr-FR")}
                </div>
              )}
              <button
                data-testid="force-purge-result-dismiss"
                onClick={() => {
                  setForceStage("idle");
                  setForceResult(null);
                }}
                className="mt-1 text-xs text-green-800 underline hover:no-underline"
              >
                Fermer
              </button>
            </div>
          )}

          {forcePreview && (
            <div
              data-testid="force-purge-preview-result"
              className="bg-white border border-orange-200 rounded-lg p-3 text-sm"
            >
              <div>
                <strong>{forcePreview.operations_count}</strong> opération(s) éligible(s){" "}
                {forcePreview.force_all
                  ? "(toutes les opérations envoyées)"
                  : `(plus de ${forcePreview.interval_months} mois)`}
              </div>
              <div>
                Espace estimé libéré :{" "}
                <strong>{fmtBytes(forcePreview.media_bytes_estimate)}</strong>
              </div>
              {forcePreview.operations_count > 0 && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-orange-800">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Action irréversible. Les PDF restent accessibles via les emails déjà envoyés.</span>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Orphan cleanup */}
        <section className="bg-white rounded-xl border border-[var(--vidoc-border)] p-5 space-y-3">
          <h2 className="text-sm font-semibold">Nettoyage des opérations orphelines</h2>
          <p className="text-sm text-[var(--vidoc-text-secondary)]">
            Supprime les opérations dont le client a été effacé et qui n'ont aucun snapshot de nom.
          </p>
          <div className="flex gap-2">
            <button
              data-testid="orphan-preview-btn"
              onClick={handleOrphanPreview}
              disabled={busy}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)] text-sm font-medium touch-target"
            >
              <Eye className="w-4 h-4" />
              Prévisualiser
            </button>
            <button
              data-testid="orphan-run-btn"
              onClick={handleOrphanRun}
              disabled={busy || !orphanData?.operations_count}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--vidoc-danger)] text-white text-sm font-medium touch-target disabled:opacity-50"
            >
              <Brush className="w-4 h-4" />
              Supprimer les orphelines
            </button>
          </div>
          {orphanData && (
            <div className="bg-[var(--vidoc-bg-secondary)] rounded-lg p-3 text-sm">
              <div><strong>{orphanData.operations_count}</strong> opération(s) orpheline(s)</div>
              <div>Espace estimé : <strong>{fmtBytes(orphanData.media_bytes_estimate)}</strong></div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
